import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from "rxjs";
import { Apollo, gql } from 'apollo-angular';
import { catchError, map } from 'rxjs/operators';

export interface Partner {
  id: string;
  extPartnerId: string;
  name: string;
  document: string;
  documentTypeId: string;
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class PartnerService {
  private keyStorage = 'partner_selected'
  private currentPartner = new BehaviorSubject<string|null>(null);
  public readonly currentPartnerSelected$: Observable<string|null> = this.currentPartner.asObservable();
  
  private partnersListSubject = new BehaviorSubject<Partner[]>([]);
  public readonly partnersList$: Observable<Partner[]> = this.partnersListSubject.asObservable();
  
  constructor(private apollo: Apollo) {
    this.currentPartner.next(this.getStoredPartner());
  }

  public fetchPartners(): Observable<Partner[]> {
    return this.apollo
      .watchQuery<any>({
        query: gql`
          {
            partnersByUser {
              id
              extPartnerId
              name
              document
              documentTypeId
              createdAt
              updatedAt
            }
          }
        `
      })
      .valueChanges
      .pipe(
        map(result => {
          const partners = result.data.partnersByUser;
          this.partnersListSubject.next(partners);
          return partners;
        }),
      );
  }

  public getPartners(): Partner[] {
    return this.partnersListSubject.getValue();
  }

  public selectPartner(partnerId: string): void {
    this.storePartner(partnerId);
    this.currentPartner.next(partnerId);
  }

  public hasSelectedPartner(): boolean {
    return !!this.getStoredPartner();
  }

  public getCurrentPartner(): string | null {
    return this.getStoredPartner();
  }

  public clearPartnerSelection(): void {
    localStorage.removeItem(this.keyStorage);
    this.currentPartner.next(null);
  }

  private storePartner(partnerId: string): void {
    localStorage.setItem(this.keyStorage, JSON.stringify(partnerId));
  }
  
  private getStoredPartner(): string | null {
    const partner = localStorage.getItem(this.keyStorage);
    return partner ? JSON.parse(partner) : null;
  }
}